import { FC } from "react";

import {
  Badge,
  BadgeProps,
  Box,
  DeleteIcon,
  DraftIcon,
  JourneyTemplateIcon,
  PauseIcon,
  PlayIcon,
  RefreshIcon,
  StopIcon,
  Tooltip,
} from "@hightouch/htui";
import capitalize from "lodash/capitalize";

import { JourneyState, JourneyTemplateState } from "src/pages/journeys/types";

const STATUS_MAPPING: Record<
  JourneyState | JourneyTemplateState,
  {
    variant: BadgeProps["variant"];
    icon: NonNullable<BadgeProps["svgIcon"]>;
    message: string;
    isPending?: boolean;
    label?: string;
  }
> = {
  active: {
    variant: "info",
    icon: PlayIcon,
    message: "Journey is currently running",
  },
  deleting: {
    variant: "subtle",
    icon: DeleteIcon,
    label: "Deletion in progress",
    message: "Journey is being deleted",
    isPending: true,
  },
  draft: {
    variant: "ghost",
    icon: DraftIcon,
    message: "Journey that has not run yet",
    isPending: true,
  },
  draining: {
    variant: "info",
    icon: PlayIcon,
    message:
      "Not accepting new entries but existing members will complete the journey",
  },
  off: {
    variant: "subtle",
    icon: StopIcon,
    message: "Journey is not accepting new entries and all members have exited",
  },
  paused: {
    variant: "warning",
    icon: PauseIcon,
    message:
      "Journey is not accepting new entries and existing members remain in their current tile",
  },
  resetting: {
    variant: "subtle",
    icon: RefreshIcon,
    message: "Journey is being reset",
  },
  staged: {
    variant: "info",
    icon: DraftIcon,
    message: "Journey is being edited in staging mode",
  },
  template: {
    variant: "info",
    icon: JourneyTemplateIcon,
    message: "A template used for creating new journeys from",
  },
};

type Props = {
  status: JourneyState | JourneyTemplateState;
  size?: BadgeProps["size"];
  shape?: BadgeProps["shape"];
};

export const JourneyStatusBadge: FC<Props> = ({ status, size, shape }) => {
  const badge = STATUS_MAPPING[status];

  if (!badge) {
    return null;
  }

  return (
    <Tooltip message={badge.message}>
      <Box flexShrink={0}>
        {shape === "circle" ? (
          <Badge
            shape="circle"
            svgIcon={badge.icon}
            aria-label={badge.label ?? capitalize(status)}
            isPending={badge.isPending}
            size={size}
            variant={badge.variant}
          />
        ) : (
          <Badge
            isPending={badge.isPending}
            size={size}
            variant={badge.variant}
          >
            {badge.label ?? capitalize(status)}
          </Badge>
        )}
      </Box>
    </Tooltip>
  );
};
